import { makeStyles } from "tss-react/mui";
import { palette, radius, shadow, withAlpha } from "@/themes/tokens";

// Modern header: sticky white bar with pill links; side panel for narrow windows; floating tab bar on phones.
const useStyles = makeStyles()((theme) => {
    const brand = theme.palette.primary.main;
    return {
        header: {
            position: "sticky",
            top: 0,
            zIndex: 1100,
            backgroundColor: withAlpha(palette.surface, 0.92) as string,
            backdropFilter: "saturate(180%) blur(12px)",
            borderBottom: `1px solid ${palette.border}`,
        },
        // Slim strip above the bar linking back to the vendor's own website.
        siteStrip: { backgroundColor: palette.surfaceMuted, borderBottom: `1px solid ${palette.border}` },
        siteStripInner: { maxWidth: 1280, margin: "0 auto", height: 30, padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "flex-end", [theme.breakpoints.down("md")]: { padding: "0 16px", justifyContent: "center" } },
        siteLink: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 500, color: `${palette.textMuted} !important`, cursor: "pointer", textDecoration: "none", "&:hover": { color: `${palette.text} !important`, textDecoration: "underline" } },
        bar: {
            maxWidth: 1280,
            margin: "0 auto",
            height: 72,
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            gap: 24,
            [theme.breakpoints.down("md")]: { height: 64, padding: "0 16px", gap: 12 },
        },
        logoWrap: { display: "flex", alignItems: "center", flexShrink: 0 },
        logoImg: {
            height: 44,
            maxWidth: 200,
            objectFit: "contain",
            cursor: "pointer",
            [theme.breakpoints.down("md")]: { height: 36, maxWidth: 150 },
        },
        links: { display: "flex", alignItems: "center", gap: 4, flex: 1 },
        navLink: {
            position: "relative",
            border: 0,
            background: "transparent",
            color: palette.textMuted,
            fontWeight: 600,
            fontSize: 15,
            padding: "8px 16px",
            borderRadius: radius.pill,
            cursor: "pointer",
            transition: "background-color .15s, color .15s",
            "&:hover": { backgroundColor: palette.surfaceMuted, color: palette.text },
        },
        navLinkActive: {
            color: `${palette.text} !important`,
            backgroundColor: withAlpha(brand, 0.14) as string,
            "&:hover": { backgroundColor: withAlpha(brand, 0.2) as string },
        },
        dot: {
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: palette.danger,
            marginLeft: 6,
            verticalAlign: "middle",
        },
        actions: { display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" },
        iconButton: {
            width: 42,
            height: 42,
            border: `1px solid ${palette.border}`,
            backgroundColor: palette.surface,
            color: palette.text,
            "&:hover": { backgroundColor: palette.surfaceMuted },
        },
        profileChip: {
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            height: 42,
            maxWidth: 240,
            padding: "0 10px 0 16px",
            border: `1px solid ${palette.border}`,
            borderRadius: radius.pill,
            background: palette.surface,
            color: palette.text,
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            "&:hover": { backgroundColor: palette.surfaceMuted },
        },
        profileChipText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        menuHeader: { padding: "12px 16px 10px", minWidth: 240 },
        menuTitle: { fontSize: 12, fontWeight: 700, color: palette.textMuted, textTransform: "uppercase", letterSpacing: 0.6 },
        menuSub: { fontSize: 14, color: palette.text, marginTop: 2, overflowWrap: "anywhere" },
        link: { cursor: "pointer", color: palette.textMuted, textDecoration: "underline", fontSize: 13 },

        // Side panel (narrow windows and phone "More").
        sidePanel: { width: 340, maxWidth: "92vw", borderTopLeftRadius: radius.xl, borderBottomLeftRadius: radius.xl },
        panel: { display: "flex", flexDirection: "column", gap: 12, padding: 16, minHeight: "100%" },
        panelHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 4 },
        panelSection: {
            background: palette.surface,
            border: `1px solid ${palette.border}`,
            borderRadius: radius.lg,
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 2,
        },
        panelLabel: { fontSize: 12, fontWeight: 700, color: palette.textMuted, textTransform: "uppercase", letterSpacing: 0.6, padding: "4px 8px 8px" },
        panelItem: {
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            border: 0,
            background: "transparent",
            textAlign: "left",
            padding: "12px 12px",
            borderRadius: radius.md,
            fontSize: 15,
            fontWeight: 600,
            color: palette.text,
            cursor: "pointer",
            "&:hover": { backgroundColor: palette.surfaceMuted },
        },
        panelItemActive: { backgroundColor: withAlpha(brand, 0.14) as string },
        panelIcon: { display: "inline-flex", width: 20, justifyContent: "center", color: palette.textMuted },
        countBadge: {
            minWidth: 22,
            height: 22,
            padding: "0 6px",
            borderRadius: 11,
            background: palette.danger,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
        },
        panelFooter: { marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 4px" },

        // Floating tab bar (phone-sized windows).
        bottomBar: {
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            padding: "8px 16px calc(12px + env(safe-area-inset-bottom))",
            pointerEvents: "none",
        },
        bottomBarInner: {
            pointerEvents: "auto",
            display: "flex",
            background: palette.surface,
            border: `1px solid ${palette.border}`,
            borderRadius: radius.xl,
            padding: 6,
            boxShadow: shadow.float,
            maxWidth: 520,
            margin: "0 auto",
        },
        tab: {
            flex: 1,
            border: 0,
            background: "transparent",
            borderRadius: radius.lg,
            padding: "8px 4px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            color: palette.textMuted,
            cursor: "pointer",
            minWidth: 0,
        },
        tabActive: { backgroundColor: withAlpha(brand, 0.14) as string, color: palette.text },
        tabIcon: { position: "relative", display: "inline-flex", fontSize: 18 },
        tabDot: { position: "absolute", top: -2, right: -6, width: 8, height: 8, borderRadius: 4, background: palette.danger },
        tabLabel: { fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" },
    };
});

export default useStyles;
